import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  DatePicker,
  Descriptions,
  Empty,
  Input,
  Modal,
  Pagination,
  Segmented,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType, TablePaginationConfig } from "antd/es/table";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "dayjs/locale/zh-cn";
import { AuditOutlined, ReloadOutlined } from "../components/icons";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "../../server/routers";
import { trpc } from "../trpc";
import {
  buildMemberLabelMap,
  buildRoleDetailViews,
  formatNullableCount,
  formatOverwriteMode,
  formatReviewActor,
  formatReviewDateTime,
  getReviewStatusMeta,
  mapChangeRows,
  mapDataCoverageItems,
  mapWriteAttemptRows,
  resolveClampedListPage,
  resolveReviewSheetTitle,
  summarizeReviewDetail,
  type ReviewDetailRecord,
  type ReviewListItem,
  type ReviewRoleDetailView,
} from "./scriptRoleReviewRecordsHelpers";
import "./scriptRoleReviewRecords.css";

dayjs.locale("zh-cn");

const { RangePicker } = DatePicker;
const { Text, Title, Paragraph } = Typography;

type ReviewStatusFilter = "writing" | "completed" | "failed";

type DraftFilters = {
  keyword: string;
  status?: ReviewStatusFilter;
  group: string;
  confirmedRange: [Dayjs, Dayjs] | null;
};

type AppliedFilters = {
  keyword?: string;
  status?: ReviewStatusFilter;
  group?: string;
  confirmedFrom?: string;
  confirmedTo?: string;
};

const STATUS_OPTIONS: Array<{ value: ReviewStatusFilter; label: string }> = [
  { value: "completed", label: "已完成" },
  { value: "writing", label: "写入中" },
  { value: "failed", label: "失败" },
];

const DEFAULT_PAGE_SIZE = 20;
const ROLE_PAGE_SIZE = 20;

type ReviewRouterOutputs = inferRouterOutputs<AppRouter>["feishuSheets"];
type ReviewListResult = NonNullable<ReviewRouterOutputs["listRoleAssignmentReviews"]>;
type ReviewDetailResult = NonNullable<ReviewRouterOutputs["getRoleAssignmentReview"]>;

function createEmptyDraftFilters(): DraftFilters {
  return {
    keyword: "",
    status: undefined,
    group: "",
    confirmedRange: null,
  };
}

function toAppliedFilters(draft: DraftFilters): AppliedFilters {
  const keyword = draft.keyword.trim();
  const group = draft.group.trim();
  const [from, to] = draft.confirmedRange ?? [];
  return {
    keyword: keyword || undefined,
    status: draft.status || undefined,
    group: group || undefined,
    confirmedFrom: from ? from.startOf("day").toISOString() : undefined,
    confirmedTo: to ? to.endOf("day").toISOString() : undefined,
  };
}

function RoleReviewCard(props: { role: ReviewRoleDetailView }) {
  const { role } = props;
  return (
    <article className={`script-role-review-role-card${role.changed ? " is-changed" : ""}`}>
      <header className="script-role-review-role-header">
        <div className="script-role-review-role-meta">
          <Tag color="blue">{role.speakerLabel || `气氛员${role.tempRoleId}`}</Tag>
          <Tag>{role.contentBeatLabel}</Tag>
          {role.changed ? <Tag color="gold">人工修改</Tag> : <Tag color="green">保留 AI</Tag>}
          <Text type="secondary">角色 ID：{role.tempRoleId}</Text>
        </div>
        <Text type="secondary">{role.utteranceCount || role.scriptLines.length} 句</Text>
      </header>

      <div className="script-role-review-role-grid">
        <section className="script-role-review-section">
          <div className="script-role-review-section-title">
            <strong>完整话术 / 画像 / AI 原匹配</strong>
          </div>

          {role.scriptLines.length > 0 ? (
            <div className="script-role-review-lines">
              {role.scriptLines.map((line) => (
                <div className="script-role-review-line" key={`${role.tempRoleId}-${line.rowIndex}`}>
                  <div className="script-role-review-line-number">第 {line.rowNumber} 行</div>
                  <div className="script-role-review-line-text">
                    {line.text || (line.imageToken ? `[图片 ${line.imageToken}]` : "（空内容）")}
                    {line.imageUnderstanding ? (
                      <div>
                        <Text type="secondary">图片理解：{line.imageUnderstanding}</Text>
                      </div>
                    ) : null}
                  </div>
                  <div className="script-role-review-line-d">D：{line.columnD}</div>
                </div>
              ))}
            </div>
          ) : (
            <Text type="secondary">无可用话术行</Text>
          )}

          {role.personaSummary || role.toneTags.length > 0 || role.topicTags.length > 0 ? (
            <div style={{ marginTop: 10 }}>
              <Text type="secondary">角色画像</Text>
              <Paragraph style={{ marginBottom: 6 }}>
                {role.personaSummary || "未生成角色画像"}
              </Paragraph>
              <div className="script-role-review-tags">
                {role.toneTags.map((tag) => (
                  <Tag key={`tone-${role.tempRoleId}-${tag}`}>{tag}</Tag>
                ))}
                {role.topicTags.map((tag) => (
                  <Tag key={`topic-${role.tempRoleId}-${tag}`}>{tag}</Tag>
                ))}
              </div>
            </div>
          ) : null}

          {role.originalAssignment ? (
            <div style={{ marginTop: 10 }}>
              <Text type="secondary">AI 原匹配</Text>
              <div>
                <Text strong>
                  {(role.originalAssignment.number || "无编号") +
                    " " +
                    (role.originalAssignment.nickname || role.originalAssignment.wxid || "—")}
                </Text>
                {role.originalAssignment.score != null ? (
                  <Text style={{ marginLeft: 8 }}>{role.originalAssignment.score} 分</Text>
                ) : null}
              </div>
              {role.originalAssignment.reason ? (
                <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 6 }}>
                  {role.originalAssignment.reason}
                </Paragraph>
              ) : null}
              {role.originalAssignment.scoreBreakdown.length > 0 ? (
                <div className="script-role-review-score-list">
                  {role.originalAssignment.scoreBreakdown.map((dimension) => (
                    <span
                      key={`${role.tempRoleId}-${dimension.key}`}
                      className={`script-role-review-score-chip${
                        dimension.score < 0 ? " is-negative" : ""
                      }`}
                      title={dimension.reason || undefined}
                    >
                      <em>{dimension.label}</em>
                      <strong>{dimension.score}</strong>
                      {dimension.maxScore != null && dimension.maxScore > 0 ? (
                        <small>/{dimension.maxScore}</small>
                      ) : null}
                    </span>
                  ))}
                </div>
              ) : (
                <Text type="secondary">暂无六维评分</Text>
              )}
            </div>
          ) : (
            <div style={{ marginTop: 10 }}>
              <Text type="danger">缺少 AI 原始匹配记录</Text>
            </div>
          )}
        </section>

        <section className="script-role-review-section">
          <div className="script-role-review-section-title">
            <strong>最终成员资料</strong>
            <span>{role.changed ? "人工修改" : "保留 AI"}</span>
          </div>
          {role.finalMember ? (
            <div className="script-role-review-member">
              <div className="script-role-review-member-name">
                {(role.finalMember.number || "无编号") +
                  " " +
                  (role.finalMember.nickname || role.finalMember.wxid || "—")}
              </div>
              <div className="script-role-review-member-meta">
                <div>{role.finalMember.category}</div>
                <div>{role.finalMember.deviceName}</div>
                <div>{role.finalMember.wxid || "—"}</div>
              </div>
              <div className="script-role-review-member-profile">
                {role.finalMember.personaSummary || "未提取人设摘要"}
              </div>
              {role.finalMember.compactProfile ? (
                <div className="script-role-review-member-profile">{role.finalMember.compactProfile}</div>
              ) : null}
              {role.finalMember.notes ? (
                <div className="script-role-review-member-meta">备注：{role.finalMember.notes}</div>
              ) : null}
              {role.finalMember.tags.length > 0 ? (
                <div className="script-role-review-tags">
                  {role.finalMember.tags.map((tag) => (
                    <Tag key={`${role.tempRoleId}-member-${tag}`}>{tag}</Tag>
                  ))}
                </div>
              ) : null}
            </div>
          ) : (
            <Text type="secondary">无最终成员资料</Text>
          )}
        </section>
      </div>
    </article>
  );
}

export function ScriptRoleReviewRecordsPage() {
  const { message } = App.useApp();
  const [draftFilters, setDraftFilters] = useState<DraftFilters>(() => createEmptyDraftFilters());
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [items, setItems] = useState<ReviewListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [detail, setDetail] = useState<ReviewDetailRecord | null>(null);
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
  const [roleView, setRoleView] = useState<"all" | "changed">("all");
  const [rolePage, setRolePage] = useState(1);
  const listRequestSeqRef = useRef(0);
  const detailRequestSeqRef = useRef(0);

  const loadList = useCallback(async () => {
    const requestSeq = ++listRequestSeqRef.current;
    setLoading(true);
    try {
      const result: ReviewListResult | null | undefined =
        await trpc.feishuSheets.listRoleAssignmentReviews.query({
          page,
          pageSize,
          ...appliedFilters,
        });
      if (requestSeq !== listRequestSeqRef.current) return;

      const listResult: ReviewListResult = result ?? {
        items: [],
        total: 0,
        page,
        pageSize,
      };
      const nextItems = listResult.items as ReviewListItem[];
      const nextTotal = listResult.total;
      const nextPageSize = listResult.pageSize > 0 ? listResult.pageSize : pageSize;
      const responsePage = listResult.page > 0 ? listResult.page : page;
      const clampedPage = resolveClampedListPage({
        page: responsePage,
        pageSize: nextPageSize,
        total: nextTotal,
        itemCount: nextItems.length,
      });

      if (clampedPage != null && clampedPage !== page) {
        // Avoid flashing stale previous-page rows while reloading the clamped page.
        setItems([]);
        setTotal(nextTotal);
        setPage(clampedPage);
        if (nextPageSize !== pageSize) {
          setPageSize(nextPageSize);
        }
        return;
      }

      setItems(nextItems);
      setTotal(nextTotal);
      if (responsePage !== page) {
        setPage(responsePage);
      }
      if (nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
      }
    } catch (error: unknown) {
      if (requestSeq !== listRequestSeqRef.current) return;
      message.error(error instanceof Error ? error.message : "加载审查记录失败");
      setItems([]);
      setTotal(0);
    } finally {
      if (requestSeq === listRequestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [appliedFilters, message, page, pageSize]);

  useEffect(() => {
    void loadList();
    return () => {
      listRequestSeqRef.current += 1;
    };
  }, [loadList]);

  const handleSearch = () => {
    setAppliedFilters(toAppliedFilters(draftFilters));
    setPage(1);
  };

  const handleReset = () => {
    const empty = createEmptyDraftFilters();
    setDraftFilters(empty);
    setAppliedFilters({});
    setPage(1);
  };

  const openDetail = async (reviewId: string) => {
    const requestSeq = ++detailRequestSeqRef.current;
    setActiveReviewId(reviewId);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    setRoleView("all");
    setRolePage(1);
    try {
      const result: ReviewDetailResult | null | undefined =
        await trpc.feishuSheets.getRoleAssignmentReview.query({ reviewId });
      if (requestSeq !== detailRequestSeqRef.current) return;
      if (!result) {
        setDetailError("未找到审查记录");
        return;
      }
      // Detail still goes through defensive helpers that accept a wider historical shape.
      setDetail(result as ReviewDetailRecord);
    } catch (error: unknown) {
      if (requestSeq !== detailRequestSeqRef.current) return;
      setDetailError(error instanceof Error ? error.message : "加载审查详情失败");
    } finally {
      if (requestSeq === detailRequestSeqRef.current) {
        setDetailLoading(false);
      }
    }
  };

  const closeDetail = () => {
    detailRequestSeqRef.current += 1;
    setDetailOpen(false);
    setDetailLoading(false);
    setDetailError(null);
    setDetail(null);
    setActiveReviewId(null);
    setRoleView("all");
    setRolePage(1);
  };

  useEffect(() => {
    return () => {
      detailRequestSeqRef.current += 1;
    };
  }, []);

  const memberByWxid = useMemo(() => buildMemberLabelMap(detail), [detail]);
  const roleViews = useMemo(
    () => buildRoleDetailViews(detail, { onlyChanged: roleView === "changed" }),
    [detail, roleView]
  );
  const pagedRoleViews = useMemo(() => {
    const start = (rolePage - 1) * ROLE_PAGE_SIZE;
    return roleViews.slice(start, start + ROLE_PAGE_SIZE);
  }, [rolePage, roleViews]);
  const summary = useMemo(() => summarizeReviewDetail(detail), [detail]);
  const submittedChangeRows = useMemo(
    () => mapChangeRows(detail?.submittedChangeHistory, memberByWxid),
    [detail?.submittedChangeHistory, memberByWxid]
  );
  const netChangeRows = useMemo(
    () => mapChangeRows(detail?.changes, memberByWxid),
    [detail?.changes, memberByWxid]
  );
  const writeAttemptRows = useMemo(
    () => mapWriteAttemptRows(detail?.writeAttempts),
    [detail?.writeAttempts]
  );
  const coverageItems = useMemo(
    () => mapDataCoverageItems(detail?.dataCoverage),
    [detail?.dataCoverage]
  );

  useEffect(() => {
    setRolePage(1);
  }, [roleView, activeReviewId]);

  const columns: ColumnsType<ReviewListItem> = [
    {
      title: "确认时间",
      dataIndex: "confirmedAt",
      width: 170,
      render: (value: string | null | undefined) => formatReviewDateTime(value),
    },
    {
      title: "表格标题",
      dataIndex: "sheetTitle",
      width: 220,
      ellipsis: true,
      render: (_value, record) => resolveReviewSheetTitle(record),
    },
    {
      title: "分组",
      dataIndex: "group",
      width: 120,
      ellipsis: true,
      render: (value: string | null | undefined) => value || "—",
    },
    {
      title: "审查人",
      dataIndex: "confirmedBy",
      width: 140,
      ellipsis: true,
      render: (value) => formatReviewActor(value),
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 100,
      render: (value: string) => {
        const meta = getReviewStatusMeta(value);
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "角色数",
      dataIndex: "roleCount",
      width: 90,
      render: (value: number | null | undefined) => formatNullableCount(value),
    },
    {
      title: "修改数",
      dataIndex: "changedRoleCount",
      width: 90,
      render: (value: number | null | undefined) => formatNullableCount(value),
    },
    {
      title: "写入数",
      dataIndex: "updatedCells",
      width: 90,
      render: (value: number | null | undefined) => formatNullableCount(value),
    },
    {
      title: "模式",
      dataIndex: "overwriteMode",
      width: 110,
      render: (value: boolean | null | undefined) => formatOverwriteMode(value),
    },
    {
      title: "操作",
      key: "actions",
      fixed: "right",
      width: 90,
      render: (_value, record) => (
        <Button type="link" size="small" onClick={() => void openDetail(record.reviewId)}>
          详情
        </Button>
      ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const nextPage = pagination.current ?? 1;
    const nextPageSize = pagination.pageSize ?? DEFAULT_PAGE_SIZE;
    setPage(nextPage);
    setPageSize(nextPageSize);
  };

  return (
    <div className="script-role-review-records">
      <Space orientation="vertical" size={16} style={{ width: "100%" }}>
        <div className="page-title-row" style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              审查记录
            </Title>
            <Text type="secondary">管理员只读查看角色匹配审查与飞书写入轨迹</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={() => void loadList()} loading={loading}>
            刷新
          </Button>
        </div>

        <div className="script-role-review-records-filters">
          <Input
            allowClear
            placeholder="审查号 / 标题 / 审查人"
            value={draftFilters.keyword}
            onChange={(event) =>
              setDraftFilters((current) => ({ ...current, keyword: event.target.value }))
            }
            onPressEnter={handleSearch}
            style={{ width: 240 }}
          />
          <Select
            allowClear
            placeholder="状态"
            options={STATUS_OPTIONS}
            value={draftFilters.status}
            onChange={(value) => setDraftFilters((current) => ({ ...current, status: value }))}
            style={{ width: 140 }}
          />
          <Input
            allowClear
            placeholder="分组"
            value={draftFilters.group}
            onChange={(event) =>
              setDraftFilters((current) => ({ ...current, group: event.target.value }))
            }
            onPressEnter={handleSearch}
            style={{ width: 160 }}
          />
          <RangePicker
            value={draftFilters.confirmedRange}
            onChange={(value) =>
              setDraftFilters((current) => ({
                ...current,
                confirmedRange: value && value[0] && value[1] ? [value[0], value[1]] : null,
              }))
            }
            style={{ width: 280 }}
          />
          <Button type="primary" onClick={handleSearch}>
            查询
          </Button>
          <Button onClick={handleReset}>重置</Button>
        </div>

        <Table<ReviewListItem>
          rowKey="reviewId"
          loading={loading}
          columns={columns}
          dataSource={items}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (count) => `共 ${count} 条`,
            pageSizeOptions: [10, 20, 50, 100],
          }}
          onChange={handleTableChange}
        />
      </Space>

      <Modal
        open={detailOpen}
        onCancel={closeDetail}
        footer={null}
        width="96vw"
        destroyOnHidden
        styles={{ body: { maxHeight: "82vh", overflow: "hidden" } }}
        title={
          <Space>
            <AuditOutlined />
            <span>审查详情</span>
            {activeReviewId ? (
              <Text type="secondary" style={{ fontWeight: 400, fontSize: 12 }}>
                {activeReviewId}
              </Text>
            ) : null}
          </Space>
        }
      >
        {detailLoading ? (
          <div style={{ padding: "48px 0", textAlign: "center" }}>
            <Text type="secondary">正在加载审查详情…</Text>
          </div>
        ) : detailError ? (
          <Alert type="error" showIcon title="加载失败" description={detailError} />
        ) : detail ? (
          <div className="script-role-review-detail-body">
            <Descriptions
              size="small"
              bordered
              column={{ xs: 1, sm: 2, md: 3 }}
              style={{ marginBottom: 16 }}
              items={[
                {
                  key: "title",
                  label: "表格标题",
                  children: resolveReviewSheetTitle(detail),
                },
                {
                  key: "group",
                  label: "分组",
                  children: detail.group || "—",
                },
                {
                  key: "status",
                  label: "状态",
                  children: (
                    <Tag color={getReviewStatusMeta(detail.status).color}>
                      {getReviewStatusMeta(detail.status).label}
                    </Tag>
                  ),
                },
                {
                  key: "confirmedBy",
                  label: "审查人",
                  children: formatReviewActor(detail.confirmedBy),
                },
                {
                  key: "confirmedAt",
                  label: "确认时间",
                  children: formatReviewDateTime(detail.confirmedAt),
                },
                {
                  key: "completedAt",
                  label: "完成时间",
                  children: formatReviewDateTime(detail.completedAt),
                },
                {
                  key: "mode",
                  label: "写入模式",
                  children: formatOverwriteMode(detail.overwriteMode),
                },
                {
                  key: "counts",
                  label: "数量",
                  children: `角色 ${summary.roleCount} · 修改 ${summary.changedRoleCount} · 写入 ${summary.updatedCells}`,
                },
                {
                  key: "attempts",
                  label: "尝试次数",
                  children: detail.attemptCount ?? writeAttemptRows.length,
                },
                {
                  key: "sheetId",
                  label: "Sheet ID",
                  children: detail.sheetId || "—",
                },
                {
                  key: "token",
                  label: "Spreadsheet",
                  children: detail.spreadsheetToken || "—",
                },
                {
                  key: "error",
                  label: "失败原因",
                  children: detail.lastError ? (
                    <Text type="danger">{detail.lastError}</Text>
                  ) : (
                    "—"
                  ),
                },
              ]}
            />

            <Tabs
              destroyOnHidden
              items={[
                {
                  key: "roles",
                  label: "角色审查",
                  children: (
                    <Space orientation="vertical" size={12} style={{ width: "100%" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 12,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Segmented
                          value={roleView}
                          onChange={(value) => setRoleView(value as "all" | "changed")}
                          options={[
                            { label: "全部角色", value: "all" },
                            { label: "仅看改动", value: "changed" },
                          ]}
                        />
                        <Text type="secondary">共 {roleViews.length} 个角色</Text>
                      </div>
                      {pagedRoleViews.length > 0 ? (
                        <div className="script-role-review-detail-roles">
                          {pagedRoleViews.map((role) => (
                            <RoleReviewCard key={role.tempRoleId} role={role} />
                          ))}
                        </div>
                      ) : (
                        <div className="script-role-review-empty">
                          <Empty description={roleView === "changed" ? "没有人工修改的角色" : "暂无角色审查数据"} />
                        </div>
                      )}
                      {roleViews.length > ROLE_PAGE_SIZE ? (
                        <Pagination
                          size="small"
                          current={rolePage}
                          pageSize={ROLE_PAGE_SIZE}
                          total={roleViews.length}
                          onChange={setRolePage}
                          showSizeChanger={false}
                          style={{ textAlign: "right" }}
                        />
                      ) : null}
                    </Space>
                  ),
                },
                {
                  key: "changes",
                  label: "修改记录",
                  children: (
                    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                      <div>
                        <Title level={5} style={{ marginTop: 0 }}>
                          提交过程修改轨迹
                        </Title>
                        <Table
                          size="small"
                          rowKey="key"
                          pagination={false}
                          locale={{ emptyText: "无提交过程修改轨迹" }}
                          dataSource={submittedChangeRows}
                          columns={[
                            { title: "角色", dataIndex: "tempRoleId", width: 100 },
                            { title: "从", dataIndex: "fromLabel" },
                            { title: "到", dataIndex: "toLabel" },
                            { title: "时间", dataIndex: "changedAt", width: 180 },
                          ]}
                          scroll={{ x: 720 }}
                        />
                      </div>
                      <div>
                        <Title level={5}>最终净修改</Title>
                        <Table
                          size="small"
                          rowKey="key"
                          pagination={false}
                          locale={{ emptyText: "无最终净修改" }}
                          dataSource={netChangeRows}
                          columns={[
                            { title: "角色", dataIndex: "tempRoleId", width: 100 },
                            { title: "从", dataIndex: "fromLabel" },
                            { title: "到", dataIndex: "toLabel" },
                            { title: "操作人", dataIndex: "changedBy", width: 120 },
                            { title: "时间", dataIndex: "changedAt", width: 180 },
                          ]}
                          scroll={{ x: 860 }}
                        />
                      </div>
                    </Space>
                  ),
                },
                {
                  key: "writes",
                  label: "写入记录 / 数据完整性",
                  children: (
                    <Space orientation="vertical" size={16} style={{ width: "100%" }}>
                      {detail.lastError ? (
                        <Alert
                          type="error"
                          showIcon
                          title="失败原因"
                          description={detail.lastError}
                        />
                      ) : null}
                      <div>
                        <Title level={5} style={{ marginTop: 0 }}>
                          写入尝试
                        </Title>
                        <Table
                          size="small"
                          rowKey="key"
                          pagination={false}
                          locale={{ emptyText: "无写入尝试记录" }}
                          dataSource={writeAttemptRows}
                          columns={[
                            { title: "尝试 ID", dataIndex: "attemptId", width: 220, ellipsis: true },
                            {
                              title: "状态",
                              dataIndex: "status",
                              width: 100,
                              render: (value: string) => {
                                const meta = getReviewStatusMeta(value);
                                return <Tag color={meta.color}>{meta.label}</Tag>;
                              },
                            },
                            { title: "操作人", dataIndex: "actor", width: 120 },
                            { title: "开始", dataIndex: "startedAt", width: 170 },
                            { title: "结束", dataIndex: "completedAt", width: 170 },
                            { title: "写入数", dataIndex: "updatedCells", width: 90 },
                            {
                              title: "错误",
                              dataIndex: "error",
                              render: (value: string) =>
                                value ? <Text type="danger">{value}</Text> : "—",
                            },
                          ]}
                          scroll={{ x: 1100 }}
                        />
                      </div>
                      <div>
                        <Title level={5}>数据完整性</Title>
                        {coverageItems.length > 0 ? (
                          <Descriptions
                            size="small"
                            bordered
                            column={{ xs: 1, sm: 2 }}
                            items={coverageItems.map((item) => ({
                              key: item.key,
                              label: item.label,
                              children: item.extra ? `${item.value}（${item.extra}）` : item.value,
                            }))}
                          />
                        ) : (
                          <Empty description="无数据完整性信息" />
                        )}
                      </div>
                    </Space>
                  ),
                },
              ]}
            />
          </div>
        ) : (
          <Empty description="暂无详情" />
        )}
      </Modal>
    </div>
  );
}
